import type { GridInitialState } from '@mui/x-data-grid-pro';

const WORKFLOW_FIELD = 'workflow';
const STAGE_FIELD = 'stage';

/**
 * Migration: Ensures the `workflow` column appears immediately before the
 * `stage` column in the user's saved column order.
 *
 * - If `workflow` is missing: inject it right before `stage`.
 *   If `stage` is also missing, append `workflow` at the end (extremely rare).
 * - If `workflow` is present but not directly before `stage`: move it.
 * - Otherwise no-op.
 *
 * Preserves all other column settings (dimensions, visibility, etc.).
 */
export function addWorkflowColumn(
  state: GridInitialState
): { next: GridInitialState; changed: boolean } {
  const original = state.columns?.orderedFields;
  if (!original?.length) {
    return { next: state, changed: false };
  }

  const idxWorkflow = original.indexOf(WORKFLOW_FIELD);
  const idxStage = original.indexOf(STAGE_FIELD);

  let ordered: string[];

  if (idxWorkflow === -1) {
    if (idxStage !== -1) {
      ordered = [
        ...original.slice(0, idxStage),
        WORKFLOW_FIELD,
        ...original.slice(idxStage),
      ];
    } else {
      ordered = [...original, WORKFLOW_FIELD];
    }
  } else if (idxStage !== -1 && idxWorkflow !== idxStage - 1) {
    const withoutWorkflow = original.filter((field) => field !== WORKFLOW_FIELD);
    const newIdxStage = withoutWorkflow.indexOf(STAGE_FIELD);
    ordered = [
      ...withoutWorkflow.slice(0, newIdxStage),
      WORKFLOW_FIELD,
      ...withoutWorkflow.slice(newIdxStage),
    ];
  } else {
    return { next: state, changed: false };
  }

  const changed =
    ordered.length !== original.length ||
    ordered.some((field, index) => field !== original[index]);

  if (!changed) {
    return { next: state, changed: false };
  }

  return {
    next: {
      ...state,
      columns: {
        ...state.columns,
        orderedFields: ordered,
      },
    },
    changed: true,
  };
}
